import type { DsdEditorChunk } from "@/apis/dsd-editor/route";
import type {
  PlainDsdCell,
  PlainDsdColGroup,
  PlainDsdRow,
  PlainDsdTable,
} from "@/types/plain-dsd-table";

export type DsdEditorParsedItem =
  | {
      id: string;
      kind: "table";
      sectionName: string;
      chunkId: string;
      preview: string;
      table: PlainDsdTable;
    }
  | {
      id: string;
      kind: "paragraph";
      sectionName: string;
      chunkId: string;
      subChunkIndex: number;
      paragraphIndex: number;
      preview: string;
      text: string;
      /** HTML 요소의 data-dsd-id 등. 있으면 이 문단을 value item으로 편집 가능 */
      valueItemId?: string;
    };

export interface DsdEditorSelectedCellSummary {
  cellId: string;
  value: string;
  sectionName: string;
}

export const getChunkSectionName = (
  chunk: DsdEditorChunk,
  sections: string[],
) =>
  sections.find((section) => section === chunk.title || section === chunk.id) ??
  chunk.title;

const parsePositiveInteger = (value: string | null) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

const getElementAttrs = (element: Element): Record<string, string> =>
  Object.fromEntries(
    Array.from(element.attributes).map((attribute) => [
      attribute.name,
      attribute.value,
    ]),
  );

const getHtmlCellId = (
  cell: HTMLTableCellElement,
  fallbackPrefix: string,
  rowIndex: number,
  cellIndex: number,
) =>
  cell.id ||
  cell.getAttribute("data-cell-id") ||
  cell.getAttribute("data-value-item-ref") ||
  // 구 속성명 폴백 (data-dsd-id → data-value-item-ref 전환기)
  cell.getAttribute("data-dsd-id") ||
  cell.getAttribute("origin-dsd-id") ||
  `${fallbackPrefix}_r${rowIndex}_c${cellIndex}`;

// 문단 요소의 value item ref(= data-value-item-ref 등). 없으면 undefined → 편집 불가로 처리.
const getElementNodeId = (element: Element | null): string | undefined =>
  element?.getAttribute("data-value-item-ref") ||
  // 구 속성명 폴백 (data-dsd-id → data-value-item-ref 전환기)
  element?.getAttribute("data-dsd-id") ||
  element?.getAttribute("data-node-id") ||
  element?.getAttribute("origin-dsd-id") ||
  element?.id ||
  undefined;

const getColumnName = (columnIndex: number) => {
  let remaining = columnIndex + 1;
  let name = "";

  while (remaining > 0) {
    const modulo = (remaining - 1) % 26;
    name = String.fromCharCode(65 + modulo) + name;
    remaining = Math.floor((remaining - modulo) / 26);
  }

  return name;
};

const getCellAddress = (rowIndex: number, columnIndex: number) =>
  `${getColumnName(columnIndex)}${rowIndex + 1}`;

const parseHtmlTableCell = (
  cell: HTMLTableCellElement,
  fallbackPrefix: string,
  rowIndex: number,
  cellIndex: number,
  columnIndex: number,
): PlainDsdCell => {
  const tagName = cell.tagName.toLowerCase() === "th" ? "th" : "td";
  const attrs = getElementAttrs(cell);
  const fallbackAddress = getCellAddress(rowIndex, columnIndex);

  return {
    cellId: getHtmlCellId(cell, fallbackPrefix, rowIndex, cellIndex),
    value: cell.textContent?.trim() ?? "",
    rawValue: cell.textContent ?? "",
    tagName,
    style: cell.getAttribute("style"),
    attrs: {
      ...attrs,
      "data-dsd-editor-address":
        attrs["data-address"] ??
        attrs["data-cell-address"] ??
        attrs["data-dsd-address"] ??
        fallbackAddress,
    },
    rowspan: parsePositiveInteger(cell.getAttribute("rowspan")),
    colspan: parsePositiveInteger(cell.getAttribute("colspan")),
    isHeader: tagName === "th",
    isGhost: false,
  };
};

const parseHtmlTableColGroups = (table: HTMLTableElement): PlainDsdColGroup[] =>
  Array.from(table.querySelectorAll(":scope > colgroup")).map((colgroup) => ({
    cols: Array.from(colgroup.querySelectorAll(":scope > col")).map((col) => {
      const width = Number(col.getAttribute("width"));
      return { width: Number.isInteger(width) && width > 0 ? width : null };
    }),
  }));

const parseHtmlTable = (
  table: HTMLTableElement,
  itemId: string,
): PlainDsdTable | null => {
  const occupiedColumnsByRow = new Map<number, Set<number>>();

  const rows: PlainDsdRow[] = Array.from(table.rows)
    .map((row, rowIndex): PlainDsdRow | null => {
      const occupiedColumns =
        occupiedColumnsByRow.get(rowIndex) ?? new Set<number>();
      occupiedColumnsByRow.set(rowIndex, occupiedColumns);
      let columnIndex = 0;

      const cells = Array.from(row.cells).map((cell, cellIndex) => {
        while (occupiedColumns.has(columnIndex)) {
          columnIndex += 1;
        }

        const parsedCell = parseHtmlTableCell(
          cell,
          itemId,
          rowIndex,
          cellIndex,
          columnIndex,
        );

        for (
          let rowOffset = 0;
          rowOffset < parsedCell.rowspan;
          rowOffset += 1
        ) {
          const targetRow = rowIndex + rowOffset;
          const targetOccupiedColumns =
            occupiedColumnsByRow.get(targetRow) ?? new Set<number>();
          occupiedColumnsByRow.set(targetRow, targetOccupiedColumns);

          for (
            let columnOffset = 0;
            columnOffset < parsedCell.colspan;
            columnOffset += 1
          ) {
            targetOccupiedColumns.add(columnIndex + columnOffset);
          }
        }

        columnIndex += parsedCell.colspan;

        return parsedCell;
      });

      return cells.length > 0
        ? { cells, attrs: getElementAttrs(row), isGhost: false }
        : null;
    })
    .filter((row): row is PlainDsdRow => row !== null);

  if (rows.length === 0) return null;

  return {
    tableId: table.id || itemId,
    rows,
    attrs: getElementAttrs(table),
    colGroups: parseHtmlTableColGroups(table),
  };
};

const parseHtmlParagraphText = (text: string) => {
  const normalizedText = text.trim();
  return normalizedText || null;
};

const isHtmlAttributeBoundary = (text: string, quoteIndex: number) => {
  const rest = text.slice(quoteIndex + 1);
  const trimmedRest = rest.trimStart();

  return (
    trimmedRest.startsWith(">") ||
    trimmedRest.startsWith("/>") ||
    /^[A-Za-z_:][\w:.-]*\s*=/.test(trimmedRest)
  );
};

const escapeUnquotedStyleValueQuotes = (html: string) => {
  let result = "";
  let cursor = 0;

  while (cursor < html.length) {
    const styleStart = html.indexOf('style="', cursor);
    if (styleStart < 0) {
      result += html.slice(cursor);
      break;
    }

    const valueStart = styleStart + 'style="'.length;
    result += html.slice(cursor, valueStart);

    let valueEnd = valueStart;
    let normalizedStyle = "";

    while (valueEnd < html.length) {
      const char = html[valueEnd];

      if (char === '"' && isHtmlAttributeBoundary(html, valueEnd)) {
        break;
      }

      normalizedStyle += char === '"' ? "&quot;" : char;
      valueEnd += 1;
    }

    result += normalizedStyle;

    if (valueEnd >= html.length) {
      cursor = valueEnd;
      continue;
    }

    result += html[valueEnd];
    cursor = valueEnd + 1;
  }

  return result;
};

export const parseHtmlToItems = (
  html: string,
  itemPrefix: string,
  subChunkIndex: number,
  baseItem: Pick<DsdEditorParsedItem, "sectionName" | "chunkId">,
): DsdEditorParsedItem[] => {
  if (typeof DOMParser === "undefined") return [];

  const document = new DOMParser().parseFromString(
    escapeUnquotedStyleValueQuotes(html),
    "text/html",
  );
  const items: DsdEditorParsedItem[] = [];
  let tableIndex = 0;
  let paragraphIndex = 0;

  const visit = (node: Node) => {
    if (node instanceof HTMLTableElement) {
      const id = `${itemPrefix}_t${tableIndex}`;
      const table = parseHtmlTable(node, id);
      tableIndex += 1;
      if (table) {
        items.push({
          ...baseItem,
          id,
          kind: "table",
          preview: table.rows
            .flatMap((row) => row.cells.map((cell) => cell.value))
            .join(" "),
          table,
        });
      }
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = parseHtmlParagraphText(node.textContent ?? "");
      if (text) {
        items.push({
          ...baseItem,
          id: `${itemPrefix}_paragraph_${paragraphIndex}`,
          kind: "paragraph",
          subChunkIndex,
          paragraphIndex,
          preview: text,
          text,
          valueItemId: getElementNodeId(node.parentElement),
        });
        paragraphIndex += 1;
      }
      return;
    }

    if (!(node instanceof Element)) return;

    if (node.querySelector("table")) {
      node.childNodes.forEach(visit);
      return;
    }

    const text = parseHtmlParagraphText(node.textContent ?? "");
    if (text) {
      items.push({
        ...baseItem,
        id: `${itemPrefix}_paragraph_${paragraphIndex}`,
        kind: "paragraph",
        subChunkIndex,
        paragraphIndex,
        preview: text,
        text,
        valueItemId: getElementNodeId(node),
      });
      paragraphIndex += 1;
    }
  };

  document.body.childNodes.forEach(visit);

  return items;
};

export const getReportParsedItems = (
  chunks: DsdEditorChunk[],
  sections: string[],
) =>
  chunks.flatMap((chunk) => {
    const sectionName = getChunkSectionName(chunk, sections);

    return chunk.subChunks.flatMap((subChunk, subChunkOrder) => {
      const itemPrefix = `${chunk.id}:sc${subChunkOrder}`;

      return parseHtmlToItems(subChunk.html, itemPrefix, subChunkOrder, {
        sectionName,
        chunkId: chunk.id,
      });
    });
  });

export const getRenderedCellIds = (items: DsdEditorParsedItem[]) => {
  const ids = new Set<string>();

  for (const item of items) {
    if (item.kind !== "table") continue;

    for (const row of item.table.rows) {
      for (const cell of row.cells) {
        ids.add(cell.cellId);
      }
    }
  }

  return ids;
};

export const getSelectedCellSummaryById = (items: DsdEditorParsedItem[]) => {
  const map = new Map<string, DsdEditorSelectedCellSummary>();

  for (const item of items) {
    if (item.kind !== "table") continue;

    for (const row of item.table.rows) {
      for (const cell of row.cells) {
        map.set(cell.cellId, {
          cellId: cell.cellId,
          value: cell.value,
          sectionName: item.sectionName,
        });
      }
    }
  }

  return map;
};
